import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { 
    Clock, Apple, Moon, Dumbbell, HeartPulse, Droplets, Footprints, 
    Smile, Stethoscope, ChevronDown, Calendar, Trophy, TrendingUp, TrendingDown, Minus, Flame, Zap, Medal
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const tabs = [
    { key: 'highlights', de: '★', en: '★' },
    { key: 'timeline', de: 'Zeit', en: 'Time' },
    { key: 'nutrition', de: 'Essen', en: 'Food' },
    { key: 'sleep', de: 'Schlaf', en: 'Sleep' },
    { key: 'fitness', de: 'Fit', en: 'Fit' },
    { key: 'health', de: 'Health', en: 'Health' },
];

const eventIcons = {
    sleep: <Moon className="w-4 h-4 text-indigo-500" />,
    food: <Apple className="w-4 h-4 text-green-500" />,
    water: <Droplets className="w-4 h-4 text-blue-500" />,
    workout: <Dumbbell className="w-4 h-4 text-orange-500" />,
    steps: <Footprints className="w-4 h-4 text-emerald-500" />,
    symptom: <HeartPulse className="w-4 h-4 text-red-500" />,
    treatment: <Stethoscope className="w-4 h-4 text-blue-600" />,
    mood: <Smile className="w-4 h-4 text-amber-500" />,
    nanouk: <span className="text-sm">🐺</span>,
};

export default function History() {
    const { language } = useLanguage();
    const [tab, setTab] = useState('highlights');
    const [data, setData] = useState(null);
    const [highlights, setHighlights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(7);
    const t = language === 'de';

    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadData reads only tab/days which are the deps
    useEffect(() => { loadData(); }, [tab, days]);

    const loadData = async () => {
        setLoading(true);
        try {
            
            // Calculate actual days for YTD
            let queryDays = days;
            if (days === -1) {
                const now = new Date();
                const startOfYear = new Date(now.getFullYear(), 0, 1);
                queryDays = Math.ceil((now - startOfYear) / (1000 * 60 * 60 * 24));
            }
            
            let res;
            if (tab === 'highlights') {
                res = await axios.get(`${API}/api/history/highlights?days=${queryDays}`);
                setHighlights(res.data);
                setData(res.data);
            } else if (tab === 'timeline') {
                res = await axios.get(`${API}/api/history/timeline?days=${queryDays}`);
                setData(res.data);
            } else {
                res = await axios.get(`${API}/api/history/${tab}?days=${queryDays}`);
                setData(res.data);
            }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="history-page">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto space-y-4">
                <h1 className="font-display text-2xl font-bold flex items-center gap-2">
                    <Clock className="w-6 h-6 text-primary" />
                    {t ? 'Verlauf & Reports' : 'History & Reports'}
                </h1>

                {/* Tabs */}
                <div className="flex gap-1 bg-muted p-1 rounded-2xl overflow-x-auto scrollbar-hide">
                    {tabs.map(tb => (
                        <button key={tb.key} onClick={() => setTab(tb.key)}
                            className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold transition-all shrink-0 ${
                                tab === tb.key ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground'
                            }`}>
                            {t ? tb.de : tb.en}
                        </button>
                    ))}
                </div>

                {/* Period selector */}
                <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
                    {[
                        { d: 7, label: '7T' },
                        { d: 14, label: '14T' },
                        { d: 30, label: '30T' },
                        { d: 90, label: '90T' },
                        { d: 180, label: '6M' },
                        { d: -1, label: 'YTD' },
                        { d: 365, label: '1J' },
                    ].map(p => (
                        <Button key={p.label} variant={days === p.d ? 'default' : 'outline'} size="sm" className="rounded-full text-xs shrink-0" onClick={() => setDays(p.d)}>
                            {p.label}
                        </Button>
                    ))}
                </div>

                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />)}</div>
                ) : (
                    <>
                        {tab === 'highlights' && data && <HighlightsView data={data} t={t} />}
                        {tab === 'timeline' && data && <TimelineView data={data} t={t} language={language} />}
                        {tab === 'nutrition' && data && <NutritionView data={data} t={t} language={language} />}
                        {tab === 'sleep' && data && <SleepView data={data} t={t} />}
                        {tab === 'fitness' && data && <FitnessView data={data} t={t} />}
                        {tab === 'health' && data && <HealthView data={data} t={t} />}
                    </>
                )}
            </motion.div>
        </div>
    );
}

function HighlightsView({ data, t }) {
    const { highlights = [], trends = [], nanouk_summary = '', milestones = [], stats = {} } = data;
    
    return (
        <div className="space-y-4">
            {/* Nanouk Summary */}
            <Card className="border-2 border-primary/20 bg-primary/5">
                <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                        <span className="text-2xl shrink-0">🐺</span>
                        <div>
                            <p className="font-semibold text-sm mb-1">{t ? 'Nanouks Zusammenfassung' : "Nanouk's Summary"}</p>
                            <p className="text-sm text-muted-foreground leading-relaxed">{nanouk_summary}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Highlights / Records */}
            {highlights.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                    {highlights.map((h, i) => (
                        <div key={`${h.icon}-${h.label}-${i}`} className={`p-3 rounded-xl border bg-${h.color}-50 dark:bg-${h.color}-950/20 border-${h.color}-200 dark:border-${h.color}-800 text-center`}>
                            <div className="text-lg mb-1">
                                {h.icon === 'trophy' && <Trophy className="w-5 h-5 mx-auto text-amber-500" />}
                                {h.icon === 'flame' && <Flame className="w-5 h-5 mx-auto text-orange-500" />}
                                {h.icon === 'zap' && <Zap className="w-5 h-5 mx-auto text-amber-500" />}
                            </div>
                            <p className="text-lg font-bold">{h.value}</p>
                            <p className="text-[10px] text-muted-foreground">{h.label}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* Trends vs Previous Period */}
            {trends.length > 0 && (
                <Card className="border">
                    <CardContent className="p-4">
                        <p className="font-semibold text-sm mb-3 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-primary" />
                            {t ? 'Trends vs. Vorwoche' : 'Trends vs. Previous'}
                        </p>
                        <div className="space-y-2">
                            {trends.map((tr) => (
                                <div key={`trend-${tr.label}`} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                    <span className="text-sm font-medium">{tr.label}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold">{tr.current}</span>
                                        {tr.diff !== 0 && (
                                            <span className={`text-xs flex items-center gap-0.5 ${tr.positive ? 'text-green-600' : 'text-red-500'}`}>
                                                {tr.positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                                {tr.diff > 0 ? '+' : ''}{tr.diff}{tr.unit}
                                            </span>
                                        )}
                                        {tr.diff === 0 && <span className="text-xs text-muted-foreground"><Minus className="w-3 h-3" /></span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Milestones */}
            {milestones.length > 0 && (
                <Card className="border">
                    <CardContent className="p-4">
                        <p className="font-semibold text-sm mb-2">{t ? 'Meilensteine' : 'Milestones'}</p>
                        <div className="space-y-2">
                            {milestones.map((m, i) => (
                                <div key={`milestone-${i}-${(m.text || '').slice(0, 24)}`} className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                                    <Medal className="w-4 h-4 text-amber-500 shrink-0" />
                                    <span className="text-sm">{m.text}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Quick Stats */}
            {(stats.avg_sleep > 0 || stats.workouts > 0) && (
                <div className="grid grid-cols-3 gap-2">
                    <Stat label={t ? 'Ø Schlaf' : 'Avg Sleep'} value={stats.avg_sleep} unit="h" />
                    <Stat label="Workouts" value={stats.workouts} unit="" />
                    <Stat label={t ? 'Ø Schritte' : 'Avg Steps'} value={stats.avg_steps?.toLocaleString()} unit="" />
                </div>
            )}
        </div>
    );
}

function TimelineView({ data, t, language }) {
    const events = data.timeline || [];
    const summary = data.summary || {};
    
    // Group events by date
    const byDate = {};
    for (const e of events) {
        const date = e.time?.slice(0, 10) || 'unknown';
        if (!byDate[date]) byDate[date] = [];
        byDate[date].push(e);
    }
    
    // Frequency count (how often what was consumed)
    const frequency = {};
    for (const e of events) {
        if (e.type === 'food' || e.type === 'water') {
            const name = e.title;
            frequency[name] = (frequency[name] || 0) + 1;
        }
    }
    const frequentItems = Object.entries(frequency).sort((a, b) => b[1] - a[1]).slice(0, 8);
    
    return (
        <div className="space-y-3">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-2">
                <div className="text-center p-2 bg-muted rounded-xl"><p className="text-lg font-bold">{summary.total_calories}</p><p className="text-[9px] text-muted-foreground">kcal</p></div>
                <div className="text-center p-2 bg-muted rounded-xl"><p className="text-lg font-bold">{(summary.total_water_ml/1000).toFixed(1)}L</p><p className="text-[9px] text-muted-foreground">{t ? 'Wasser' : 'Water'}</p></div>
                <div className="text-center p-2 bg-muted rounded-xl"><p className="text-lg font-bold">{summary.sleep_hours}h</p><p className="text-[9px] text-muted-foreground">{t ? 'Schlaf' : 'Sleep'}</p></div>
                <div className="text-center p-2 bg-muted rounded-xl"><p className="text-lg font-bold">{summary.steps?.toLocaleString()}</p><p className="text-[9px] text-muted-foreground">{t ? 'Schritte' : 'Steps'}</p></div>
            </div>

            {/* Frequency Overview */}
            {frequentItems.length > 0 && (
                <div className="p-3 rounded-xl bg-muted/50 border border-border">
                    <p className="text-xs font-semibold mb-2">{t ? 'Häufigkeit' : 'Frequency'}</p>
                    <div className="flex flex-wrap gap-1.5">
                        {frequentItems.map(([name, count]) => (
                            <span key={`freq-${name}`} className="px-2 py-0.5 rounded-full bg-card border border-border text-[10px]">
                                {count}x {name}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Events grouped by date */}
            {events.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t ? 'Keine Einträge' : 'No entries'}</p>
            ) : (
                <div className="space-y-4">
                    {Object.entries(byDate).sort((a, b) => b[0].localeCompare(a[0])).map(([date, dayEvents]) => (
                        <div key={date}>
                            <p className="text-xs font-semibold text-muted-foreground mb-1 sticky top-0 bg-background py-1">
                                {new Date(date + 'T12:00:00').toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </p>
                            <div className="space-y-1 ml-1 border-l-2 border-border pl-3">
                                {dayEvents.map((e, i) => (
                                    <div key={`${e.type}-${e.time || ''}-${i}`} className="flex items-start gap-2 py-1">
                                        <div className="mt-0.5 shrink-0">{eventIcons[e.type] || eventIcons.nanouk}</div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{e.title}</p>
                                            {e.subtitle && <p className="text-[11px] text-muted-foreground">{e.subtitle}</p>}
                                        </div>
                                        <span className="text-[10px] text-muted-foreground shrink-0">{e.time?.slice(11, 16)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function NutritionView({ data, t, language }) {
    const avg = data.averages || {};
    const days = data.days || [];
    const [expandedDay, setExpandedDay] = useState(null);
    const [dayMeals, setDayMeals] = useState({});

    const loadDayMeals = async (date) => {
        if (dayMeals[date]) { setExpandedDay(expandedDay === date ? null : date); return; }
        try {
            const res = await axios.get(`${API}/api/history/timeline?date=${date}&days=1`);
            const foodEvents = (res.data?.timeline || []).filter(e => e.type === 'food' || e.type === 'water');
            
            // Group by meal type
            const grouped = {};
            for (const e of foodEvents) {
                const type = e.data?.meal_type || (e.type === 'water' ? 'water' : 'snack');
                if (!grouped[type]) grouped[type] = [];
                grouped[type].push(e);
            }
            setDayMeals(prev => ({...prev, [date]: grouped}));
            setExpandedDay(date);
        } catch(e) { console.error(e); }
    };

    const mealTypeLabels = { breakfast: t ? 'Frühstück' : 'Breakfast', lunch: t ? 'Mittag' : 'Lunch', dinner: t ? 'Abend' : 'Dinner', snack: 'Snacks', water: t ? 'Getränke' : 'Drinks' };

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
                <Stat label={t ? 'Ø Kalorien' : 'Avg Calories'} value={avg.calories} unit="kcal" />
                <Stat label={t ? 'Ø Protein' : 'Avg Protein'} value={avg.protein} unit="g" />
                <Stat label={t ? 'Ø Wasser' : 'Avg Water'} value={Math.round((avg.water_ml || 0)/100)/10} unit="L" />
            </div>
            <div className="space-y-1">
                {days.slice(0, 14).map((d) => {
                    const score = d.calories >= 1500 && d.protein >= 60 ? 'good' : d.calories >= 800 ? 'ok' : 'low';
                    const isExpanded = expandedDay === d.date;
                    return (
                        <div key={d.date || `${d.calories}-${d.protein}`}>
                            <button onClick={() => loadDayMeals(d.date)} className="w-full flex items-center gap-2 p-2 rounded-lg bg-muted/30 hover:bg-muted/60 transition-colors text-left">
                                <span className="text-xs text-muted-foreground w-12 shrink-0">{d.date?.slice(5)}</span>
                                <div className="flex-1 flex gap-2 text-xs items-center">
                                    <span className="font-medium w-16">{d.calories} kcal</span>
                                    <span className="text-muted-foreground w-12">{d.protein}g P</span>
                                    <span className="text-muted-foreground w-10">{Math.round(d.water_ml/100)/10}L</span>
                                </div>
                                <span className={`w-2 h-2 rounded-full shrink-0 ${score === 'good' ? 'bg-green-500' : score === 'ok' ? 'bg-amber-400' : 'bg-red-400'}`} />
                            </button>
                            {isExpanded && dayMeals[d.date] && (
                                <div className="ml-4 mt-1 mb-2 pl-3 border-l-2 border-primary/20 space-y-2">
                                    {Object.entries(dayMeals[d.date]).map(([type, items]) => (
                                        <div key={type}>
                                            <p className="text-[10px] font-semibold text-muted-foreground uppercase">{mealTypeLabels[type] || type}</p>
                                            {items.map((item, j) => (
                                                <p key={j} className="text-xs pl-2">{item.title} <span className="text-muted-foreground">{item.subtitle}</span></p>
                                            ))}
                                        </div>
                                    ))}
                                    {Object.keys(dayMeals[d.date]).length === 0 && (
                                        <p className="text-xs text-muted-foreground">{t ? 'Nur Getränke/Wasser' : 'Only drinks/water'}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Gruppierte Zusammenfassung */}
            <NutritionSummaryBlock days={days} language={language} t={t} />
        </div>
    );
}

function NutritionSummaryBlock({ days, language, t }) {
    const [foodSummary, setFoodSummary] = useState(null);
    
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/api/favorites/memory/food`);
                setFoodSummary(res.data?.suggestions || []);
            } catch (err) {
                console.warn('Failed to load food memory suggestions:', err);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    if (!foodSummary || foodSummary.length === 0) return null;
    
    // Categorize
    const categories = { protein: [], carbs: [], drinks: [], other: [] };
    for (const f of foodSummary) {
        const name = (f.name || '').toLowerCase();
        if (name.includes('wein') || name.includes('kaffee') || name.includes('cappuccino') || name.includes('bier') || name.includes('saft') || name.includes('tee') || name.includes('smoothie') || name.includes('wasser')) {
            categories.drinks.push(f);
        } else if ((f.protein || 0) > 15) {
            categories.protein.push(f);
        } else if ((f.carbs || 0) > 30) {
            categories.carbs.push(f);
        } else {
            categories.other.push(f);
        }
    }
    
    const catLabels = { protein: t ? 'Proteinreich (Fleisch/Fisch)' : 'High Protein', carbs: t ? 'Kohlenhydrate' : 'Carbs', drinks: t ? 'Getränke' : 'Drinks', other: t ? 'Sonstiges' : 'Other' };
    
    return (
        <div className="p-3 rounded-xl bg-muted/50 border border-border space-y-2">
            <p className="text-xs font-semibold">{t ? 'Deine häufigsten Lebensmittel' : 'Your Most Frequent Foods'}</p>
            {Object.entries(categories).filter(([_, items]) => items.length > 0).map(([cat, items]) => (
                <div key={cat}>
                    <p className="text-[10px] text-muted-foreground uppercase mb-1">{catLabels[cat]}</p>
                    <div className="flex flex-wrap gap-1">
                        {items.slice(0, 6).map((f) => (
                            <span key={`${cat}-${f.name}`} className="px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 text-[10px]">
                                {f.count}x {f.name}
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function SleepView({ data, t }) {
    const stats = data.stats || {};
    const nights = data.nights || [];
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
                <Stat label={t ? 'Ø Dauer' : 'Avg Duration'} value={stats.avg_duration} unit="h" />
                <Stat label={t ? 'Ø Qualität' : 'Avg Quality'} value={stats.avg_quality} unit="/10" />
                <Stat label={t ? 'Nächte' : 'Nights'} value={stats.nights_logged} unit="" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-green-50 dark:bg-green-950/20 rounded-lg text-center">
                    <span className="font-bold text-green-700">{stats.nights_over_8h}</span> <span className="text-green-600">≥8h</span>
                </div>
                <div className="p-2 bg-red-50 dark:bg-red-950/20 rounded-lg text-center">
                    <span className="font-bold text-red-700">{stats.nights_under_6h}</span> <span className="text-red-600">&lt;6h</span>
                </div>
            </div>
            <div className="space-y-1">
                {nights.slice(0, 14).map((n, i) => (
                    <div key={n.date || `night-${i}`} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                        <span className="text-xs text-muted-foreground w-16 shrink-0">{n.date?.slice(5)}</span>
                        <div className="flex-1 flex items-center gap-2">
                            <div className="h-2 rounded-full bg-indigo-200 dark:bg-indigo-900 flex-1 overflow-hidden">
                                <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(n.duration / 10 * 100, 100)}%` }} />
                            </div>
                            <span className="text-xs font-medium w-10">{n.duration}h</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground">Q{n.quality}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function FitnessView({ data, t }) {
    const stats = data.stats || {};
    const workouts = data.workouts || [];
    const [expanded, setExpanded] = useState(false);
    const [filterType, setFilterType] = useState(null);
    const [filterLocation, setFilterLocation] = useState(null);
    
    // Group workouts by date
    const filtered = workouts.filter(w => {
        if (filterType && w.type !== filterType) return false;
        if (filterLocation && (!w.notes || !w.notes.includes(filterLocation))) return false;
        return true;
    });
    
    const byDate = {};
    for (const w of filtered) {
        const date = w.date || '?';
        if (!byDate[date]) byDate[date] = [];
        byDate[date].push(w);
    }
    const dates = Object.keys(byDate).sort().reverse();
    
    // Summary data
    const byLocation = {};
    const byType = {};
    for (const w of workouts) {
        const type = w.type || w.name || 'other';
        byType[type] = (byType[type] || 0) + 1;
        if (w.notes) {
            const loc = w.notes.replace('Ort: ', '');
            if (loc && loc.length > 2 && !loc.includes('Übungen')) byLocation[loc] = (byLocation[loc] || 0) + 1;
        }
    }
    
    const activeFilter = filterType || filterLocation;
    
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
                <Stat label="Workouts" value={activeFilter ? filtered.length : stats.total_workouts} unit="" />
                <Stat label={t ? 'Stunden' : 'Hours'} value={activeFilter ? (filtered.reduce((s,w) => s+w.duration, 0)/60).toFixed(1) : stats.total_hours} unit="h" />
                <Stat label={t ? 'Ø Schritte' : 'Avg Steps'} value={stats.avg_daily_steps?.toLocaleString()} unit="" />
            </div>
            
            {/* Clickable type chips */}
            {Object.keys(byType).length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                    {activeFilter && (
                        <button onClick={() => { setFilterType(null); setFilterLocation(null); }} className="px-2 py-1 rounded-full text-[10px] font-medium bg-red-100 dark:bg-red-950/20 text-red-700 border border-red-200">
                            ✕ Filter
                        </button>
                    )}
                    {Object.entries(byType).sort((a,b) => b[1]-a[1]).map(([type, count]) => (
                        <button key={type} onClick={() => { setFilterType(filterType === type ? null : type); setFilterLocation(null); }}
                            className={`px-2 py-1 rounded-full text-[10px] font-medium border transition-all ${filterType === type ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted border-border hover:border-primary/50'}`}>
                            {count}x {type}
                        </button>
                    ))}
                </div>
            )}
            
            {/* Filtered workout list by date */}
            <div className="space-y-3">
                {dates.slice(0, expanded ? 60 : 7).map((date) => (
                    <div key={date}>
                        <p className="text-[10px] font-semibold text-muted-foreground mb-1">
                            {new Date(date + 'T12:00:00').toLocaleDateString(t ? 'de-DE' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </p>
                        <div className="space-y-1 ml-1 border-l-2 border-orange-200 dark:border-orange-800 pl-3">
                            {byDate[date].map((w, i) => (
                                <div key={w.id || `${date}-${w.type}-${i}`} className="p-2 rounded-lg bg-muted/30">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">{w.name || w.type}</span>
                                        <span className="text-xs text-muted-foreground">{w.duration >= 60 ? `${(w.duration/60).toFixed(1)}h` : `${w.duration}min`} • {w.calories} kcal</span>
                                    </div>
                                    {w.notes && <p className="text-[11px] text-muted-foreground mt-0.5">{w.notes}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {dates.length > 7 && (
                <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setExpanded(!expanded)}>
                    {expanded ? (t ? 'Weniger' : 'Less') : `${t ? 'Alle' : 'All'} ${dates.length} ${t ? 'Tage' : 'days'}`}
                </Button>
            )}

            {/* Locations summary - also clickable */}
            {Object.keys(byLocation).length > 0 && (
                <div className="p-3 rounded-xl bg-muted/50 border border-border space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase">{t ? 'Orte / Routen' : 'Locations / Routes'}</p>
                    <div className="flex flex-wrap gap-1">
                        {Object.entries(byLocation).sort((a,b) => b[1]-a[1]).slice(0,10).map(([loc, count], i) => (
                            <button key={loc} onClick={() => { setFilterLocation(filterLocation === loc ? null : loc); setFilterType(null); }}
                                className={`px-2 py-0.5 rounded-full text-[10px] border transition-all ${filterLocation === loc ? 'bg-primary text-primary-foreground border-primary' : 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800 hover:border-primary/50'}`}>
                                {count}x {loc}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function HealthView({ data, t }) {
    const stats = data.stats || {};
    const symptoms = data.symptoms || [];
    const treatments = data.treatments || [];
    const body = data.body_analyses || [];
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
                <Stat label={t ? 'Symptome' : 'Symptoms'} value={stats.total_symptoms} unit="" />
                <Stat label={t ? 'Behandlungen' : 'Treatments'} value={stats.total_treatments} unit="" />
                <Stat label={t ? 'Ø Schwere' : 'Avg Severity'} value={stats.avg_severity} unit="/10" />
            </div>
            {stats.most_frequent?.length > 0 && (
                <div>
                    <p className="text-xs font-semibold mb-1">{t ? 'Häufigste Symptome' : 'Most Frequent'}</p>
                    <div className="flex gap-2 flex-wrap">
                        {stats.most_frequent.map(([name, count]) => (
                            <span key={`sym-${name}`} className="px-2 py-1 bg-red-50 dark:bg-red-950/20 rounded-full text-[10px] text-red-700 dark:text-red-300">{name} ({count}x)</span>
                        ))}
                    </div>
                </div>
            )}
            {treatments.length > 0 && (
                <div>
                    <p className="text-xs font-semibold mb-1">{t ? 'Behandlungen' : 'Treatments'}</p>
                    {treatments.slice(0, 5).map((tr, i) => (
                        <div key={tr.id || `tr-${tr.date || ''}-${i}`} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 mb-1">
                            <Stethoscope className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                            <span className="text-xs flex-1 truncate">{tr.title}</span>
                            <span className="text-[10px] text-muted-foreground">{tr.date?.slice(5)}</span>
                        </div>
                    ))}
                </div>
            )}
            {body.length > 0 && (
                <div>
                    <p className="text-xs font-semibold mb-1">{t ? 'Körperanalysen' : 'Body Analyses'}</p>
                    {body.slice(0, 3).map((b, i) => (
                        <div key={i} className="p-2 bg-muted/30 rounded-lg mb-1 text-xs">
                            <span className="font-medium">{b.weight_kg}kg</span> | BMI {b.bmi} | KFA {b.body_fat_pct}% | Muskeln {b.muscle_mass_kg}kg
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, unit }) {
    return (
        <div className="text-center p-3 bg-muted/50 rounded-xl border border-border">
            <p className="text-xl font-bold">{value}<span className="text-xs font-normal text-muted-foreground ml-0.5">{unit}</span></p>
            <p className="text-[9px] text-muted-foreground">{label}</p>
        </div>
    );
}
